import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Upload, FileText, CheckCircle, Download, Cpu, RefreshCw,
  AlertTriangle, ChevronRight, ShieldCheck, Key, Loader2, Info,
  Smartphone, CreditCard, FlaskConical, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGENT_URL = "http://localhost:7979";

const STEPS = ["Upload", "Certificado", "Assinar", "Concluído"];

const SIGN_TYPES = [
  { value: "auto", label: "Automático (detectar pelo arquivo)" },
  { value: "PAdES", label: "PAdES — PDF com assinatura embutida" },
  { value: "XAdES", label: "XAdES — XML assinado" },
  { value: "CAdES", label: "CAdES — Documento genérico (PKCS#7)" },
];

function OtpTimer() {
  const [remaining, setRemaining] = useState(30 - (Math.floor(Date.now() / 1000) % 30));
  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(30 - (Math.floor(Date.now() / 1000) % 30));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const pct = (remaining / 30) * 100;
  const urgent = remaining <= 8;
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-7 h-7">
        <svg className="w-7 h-7 -rotate-90" viewBox="0 0 28 28">
          <circle cx="14" cy="14" r="11" fill="none" stroke="#e5e7eb" strokeWidth="3" />
          <circle
            cx="14" cy="14" r="11" fill="none"
            stroke={urgent ? "#dc2626" : "#163A24"}
            strokeWidth="3"
            strokeDasharray={`${2 * Math.PI * 11}`}
            strokeDashoffset={`${2 * Math.PI * 11 * (1 - pct / 100)}`}
            style={{ transition: "stroke-dashoffset 1s linear, stroke 0.3s" }}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold" style={{ color: urgent ? "#dc2626" : "#163A24" }}>
          {remaining}
        </span>
      </div>
      <span className={`text-xs font-medium ${urgent ? "text-red-600" : "text-gray-500"}`}>
        {urgent ? "Obtenha novo OTP!" : `Renovar em ${remaining}s`}
      </span>
    </div>
  );
}

export default function SignPage() {
  const { docId: routeDocId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [signType, setSignType] = useState("auto");
  const [dragging, setDragging] = useState(false);
  const [docId, setDocId] = useState(routeDocId || null);
  const [hashToSign, setHashToSign] = useState(null);
  const [docFilename, setDocFilename] = useState("");
  const [docSignType, setDocSignType] = useState("");

  // Certificate mode: "birdid" | "a3token" | "demo"
  const [certMode, setCertMode] = useState("birdid");
  const [agentStatus, setAgentStatus] = useState("checking");
  const [certificates, setCertificates] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);
  const [agentMockMode, setAgentMockMode] = useState(false);

  // BirdID fields
  const [otp, setOtp] = useState("");

  // A3 Token fields
  const [pin, setPin] = useState("");

  const [signing, setSigning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [otpExpired, setOtpExpired] = useState(false);
  const [visibleSignature, setVisibleSignature] = useState(true);

  const fileInputRef = useRef();

  useEffect(() => {
    if (routeDocId) {
      setDocId(routeDocId);
      prepareExistingDoc(routeDocId);
    }
  }, [routeDocId]);

  const prepareExistingDoc = async (id) => {
    try {
      const res = await axios.post(`${API}/sign/prepare/${id}`, {
        visible_signature: visibleSignature
      });
      setHashToSign(res.data.hash);
      setDocFilename(res.data.filename);
      setDocSignType(res.data.sign_type);
      setStep(1);
    } catch (e) {
      setError("Erro ao preparar documento: " + (e.response?.data?.detail || e.message));
    }
  };

  const checkAgent = useCallback(async () => {
    setAgentStatus("checking");
    try {
      const res = await fetch(`${AGENT_URL}/status`, { signal: AbortSignal.timeout(3000) });
      const data = await res.json();
      setAgentStatus("online");
      setAgentMockMode(data.mock_mode);
      const certsRes = await fetch(`${AGENT_URL}/certificates`, { signal: AbortSignal.timeout(5000) });
      const certsData = await certsRes.json();
      const certs = certsData.certificates || [];
      setCertificates(certs);
      if (certs.length > 0) setSelectedCert(certs[0].alias);
      // Auto-detect BirdID Desktop
      if (data.birdid_detected || certs.some(c => c.is_birdid)) {
        setCertMode("birdid");
      }
    } catch (e) {
      setAgentStatus("offline");
      setCertificates([]);
    }
  }, []);

  useEffect(() => { if (step === 1) checkAgent(); }, [step, checkAgent]);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer?.files[0] || e.target.files[0];
    if (dropped) setFile(dropped);
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    setError(null);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("sign_type", signType);
    try {
      const res = await axios.post(`${API}/documents/upload`, formData);
      setDocId(res.data.document_id);
      setDocFilename(res.data.filename);
      setDocSignType(res.data.sign_type);
      setStep(1);
    } catch (e) {
      setError("Erro ao enviar documento: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleSign = async () => {
    if (!docId) return;
    setSigning(true);
    setError(null);

    try {
      if (certMode === "demo") {
        const res = await axios.post(`${API}/sign/demo/${docId}`);
        setResult(res.data);
        setStep(3);
        return;
      }

      const certObj = certificates.find((c) => c.alias === selectedCert);
      if (!certObj) throw new Error("Selecione um certificado válido.");

      // Prepare payload to get the exact hash to sign (PAdES needs cert info)
      const prepRes = await axios.post(`${API}/sign/prepare/${docId}`, {
        certificate_pem: certObj.cert_pem,
        visible_signature: visibleSignature
      });
      const hashToSignLocal = prepRes.data.hash;
      setHashToSign(hashToSignLocal); // Update state for UI if needed

      // Sign via local agent (BirdID OTP or A3 PIN)
      const signPayload = {
        hash: hashToSignLocal,
        certificate_alias: selectedCert,
        pin: certMode === "birdid" ? otp : pin,
        mode: certMode,
      };

      let agentData;
      try {
        const agentRes = await axios.post(`${AGENT_URL}/sign`, signPayload, { timeout: 35000 });
        agentData = agentRes.data;
      } catch (err) {
        if (err.response && err.response.data) {
          agentData = err.response.data;
        } else {
          throw err;
        }
      }
      if (agentData.error) throw new Error(agentData.error);
      if (!agentData.signature) throw new Error("Agente não retornou assinatura");

      const completeRes = await axios.post(`${API}/sign/complete`, {
        document_id: docId,
        signature_base64: agentData.signature,
        certificate_pem: agentData.certificate_pem,
        sign_type: docSignType,
      });

      setResult({ ...completeRes.data, warning: agentData.warning });
      setStep(3);
    } catch (e) {
      let msg = e.message;
      let isOtpError = false;
      if (e.name === "AbortError" || e.name === "TimeoutError") msg = "Tempo esgotado. Verifique se o Agente Local está rodando.";
      if (e.response?.data?.detail) msg = e.response.data.detail;
      if (msg.includes("OTP") || msg.includes("PIN") || msg.includes("CKR_PIN") || msg.match(/expir|inválido|invalid/i)) {
        msg = "Código OTP inválido ou expirado. Abra o app BirdID e use o código atual.";
        isOtpError = certMode === "birdid";
      }
      setError(msg);
      if (isOtpError) {
        setOtpExpired(true);
        setOtp("");
      }
    } finally {
      setSigning(false);
    }
  };

  const handleRetryOtp = () => {
    setError(null);
    setOtpExpired(false);
    setOtp("");
    setStep(1);
  };

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/download/signed`);
      const { filename, content_base64 } = res.data;
      const bytes = atob(content_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Erro ao baixar: " + e.message);
    }
  };

  const canProceedToSign = certMode === "demo" ||
    (agentStatus === "online" && (certMode === "birdid" || certMode === "a3token"));

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Assinar Documento
        </h1>
        <p className="text-gray-500 text-sm mt-1">Assinatura digital ICP-Brasil · CAdES · PAdES · XAdES</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center mb-8" data-testid="step-indicator">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${i < step ? "step-done" : i === step ? "step-active" : "step-pending"}`}>
                {i < step ? <CheckCircle size={14} /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${i === step ? "text-[#163A24]" : "text-gray-400"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-px mx-3 transition-colors duration-300 ${i < step ? "bg-[#8FBC8F]" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700" data-testid="error-banner">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <span>{error}</span>
            {otpExpired && (
              <div className="mt-2">
                <button
                  data-testid="btn-retry-otp"
                  onClick={handleRetryOtp}
                  className="inline-flex items-center gap-1.5 bg-[#163A24] text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-[#205234] transition-colors"
                >
                  <RefreshCw size={12} /> Renovar OTP e tentar novamente
                </button>
              </div>
            )}
          </div>
          <button onClick={() => { setError(null); setOtpExpired(false); }} className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* STEP 0: Upload */}
      {step === 0 && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 space-y-5" data-testid="step-upload">
          <div
            data-testid="upload-dropzone"
            onDrop={handleFileDrop}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${dragging ? "border-[#163A24] bg-[#163A24]/5 upload-active" : file ? "border-[#8FBC8F] bg-green-50/30" : "border-gray-200 hover:border-[#163A24]/40 hover:bg-gray-50"}`}
          >
            <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.xml,.txt,.docx" onChange={handleFileDrop} data-testid="file-input" />
            {file ? (
              <>
                <FileText size={36} className="text-[#163A24] mb-3" />
                <p className="font-semibold text-[#163A24]">{file.name}</p>
                <p className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                <button className="mt-3 text-xs text-gray-400 underline" onClick={(e) => { e.stopPropagation(); setFile(null); }}>Remover</button>
              </>
            ) : (
              <>
                <Upload size={36} className="text-gray-300 mb-3" />
                <p className="font-medium text-gray-600">Arraste seu arquivo aqui</p>
                <p className="text-xs text-gray-400 mt-1">ou clique para selecionar</p>
                <p className="text-xs text-gray-300 mt-2">PDF, XML, TXT, DOCX</p>
              </>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tipo de Assinatura</label>
            <select data-testid="sign-type-select" value={signType} onChange={(e) => setSignType(e.target.value)}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#163A24] bg-white">
              {SIGN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <button data-testid="btn-proceed-upload" onClick={handleUpload} disabled={!file}
            className="w-full flex items-center justify-center gap-2 bg-[#163A24] text-white py-3 rounded-md font-semibold text-sm hover:bg-[#205234] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200">
            Prosseguir para assinatura <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* STEP 1: Certificate */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 space-y-5" data-testid="step-certificate">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-800" style={{ fontFamily: "Outfit, sans-serif" }}>Tipo de Certificado</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Arquivo: <strong>{docFilename}</strong> · <strong>{docSignType}</strong>
              </p>
            </div>
            <button data-testid="btn-refresh-agent" onClick={checkAgent} className="p-2 text-gray-400 hover:text-[#163A24] hover:bg-gray-50 rounded-md transition-colors">
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Certificate mode selector */}
          <div className="grid grid-cols-3 gap-3" data-testid="cert-mode-selector">
            {[
              { key: "birdid", icon: Smartphone, label: "BirdID Desktop", desc: "App BirdID + OTP" },
              { key: "a3token", icon: CreditCard, label: "Token / Cartão A3", desc: "PIN do hardware" },
              { key: "demo", icon: FlaskConical, label: "Demonstração", desc: "Sem validade jurídica" },
            ].map(({ key, icon: Icon, label, desc }) => (
              <button
                key={key}
                data-testid={`cert-mode-${key}`}
                onClick={() => setCertMode(key)}
                className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200 ${certMode === key ? "border-[#163A24] bg-[#163A24]/5" : "border-gray-200 hover:border-[#163A24]/40"}`}
              >
                <Icon size={22} className={certMode === key ? "text-[#163A24] mb-2" : "text-gray-400 mb-2"} />
                <p className={`text-xs font-semibold ${certMode === key ? "text-[#163A24]" : "text-gray-600"}`}>{label}</p>
                <p className="text-[10px] text-gray-400 mt-0.5 text-center">{desc}</p>
              </button>
            ))}
          </div>

          {/* Agent status (shown for birdid and a3token) */}
          {certMode !== "demo" && (
            <div data-testid="agent-status" className={`flex items-center gap-3 p-4 rounded-lg border ${agentStatus === "online" ? "bg-green-50 border-green-200" : agentStatus === "offline" ? "bg-yellow-50 border-yellow-200" : "bg-gray-50 border-gray-200"}`}>
              <Cpu size={18} className={agentStatus === "online" ? "text-green-600" : agentStatus === "offline" ? "text-yellow-600" : "text-gray-400"} />
              <div className="flex-1">
                {agentStatus === "checking" && <p className="text-sm text-gray-600">Verificando Agente Local...</p>}
                {agentStatus === "online" && (
                  <>
                    <p className="text-sm font-semibold text-green-700">Agente Local conectado</p>
                    <p className="text-xs text-green-600 mt-0.5">
                      {agentMockMode ? "Modo simulação — sem driver PKCS#11 real" : "Driver PKCS#11 detectado"}
                    </p>
                  </>
                )}
                {agentStatus === "offline" && (
                  <>
                    <p className="text-sm font-semibold text-yellow-700">Agente Local não encontrado</p>
                    <p className="text-xs text-yellow-600 mt-0.5">
                      Inicie o agente_local.py ou use <button onClick={() => setCertMode("demo")} className="underline font-medium">Demonstração</button>
                    </p>
                  </>
                )}
              </div>
              {agentStatus === "checking" && <Loader2 size={16} className="text-gray-400 animate-spin" />}
            </div>
          )}

          {/* BIRDID MODE */}
          {certMode === "birdid" && agentStatus === "online" && (
            <BirdIDForm otp={otp} setOtp={setOtp} certificates={certificates} selectedCert={selectedCert} setSelectedCert={setSelectedCert} />
          )}

          {/* A3 TOKEN MODE */}
          {certMode === "a3token" && agentStatus === "online" && (
            <A3TokenForm pin={pin} setPin={setPin} certificates={certificates} selectedCert={selectedCert} setSelectedCert={setSelectedCert} />
          )}

          {/* DEMO MODE */}
          {certMode === "demo" && (
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-blue-700">Modo Demonstração</p>
                <p className="text-xs text-blue-600 mt-0.5">Usa certificado de teste para validar o fluxo. Não tem validade jurídica.</p>
              </div>
            </div>
          )}

          {/* VISIBLE SIGNATURE OPTION */}
          {docSignType === "PAdES" && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleSignature}
                  onChange={(e) => setVisibleSignature(e.target.checked)}
                  className="w-4 h-4 text-[#163A24] rounded border-gray-300 focus:ring-[#163A24]"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Adicionar assinatura visual no rodapé</p>
                  <p className="text-[11px] text-gray-500">Inclui nome, hash e link de verificação no final do PDF</p>
                </div>
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button data-testid="btn-back-to-upload" onClick={() => setStep(0)}
              className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors">
              Voltar
            </button>
            <button data-testid="btn-proceed-to-sign" onClick={() => setStep(2)}
              disabled={!canProceedToSign || (certMode === "birdid" && otp.length > 0 && otp.length !== 6)}
              className="flex-1 flex items-center justify-center gap-2 bg-[#163A24] text-white py-2.5 rounded-md font-semibold text-sm hover:bg-[#205234] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200">
              Prosseguir <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Sign */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 space-y-5" data-testid="step-sign">
          <div>
            <h3 className="font-semibold text-gray-800" style={{ fontFamily: "Outfit, sans-serif" }}>Confirmar e Assinar</h3>
            <p className="text-xs text-gray-400 mt-0.5">Revise os dados antes de assinar</p>
          </div>

          <div className="bg-[#FAF9F6] rounded-lg p-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Arquivo</span>
              <span className="font-medium text-gray-800">{docFilename}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Padrão</span>
              <span className="font-semibold text-[#163A24]">{docSignType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Método</span>
              <span className="font-medium text-gray-700">
                {certMode === "birdid" ? "BirdID Desktop (OTP)" : certMode === "a3token" ? "Token A3 Físico (PIN)" : "Demonstração"}
              </span>
            </div>
            {certMode === "birdid" && (
              <div className="flex justify-between items-center">
                <span className="text-gray-500">OTP</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-[#163A24] text-base tracking-widest">{otp}</span>
                  <OtpTimer />
                </div>
              </div>
            )}
            <div className="pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-400 mb-1">Hash SHA-256</p>
              <p className="font-mono text-xs text-gray-600 break-all bg-white p-2 rounded border border-gray-100" data-testid="hash-display">{hashToSign}</p>
            </div>
          </div>

          {certMode === "birdid" && (
            <div className="flex items-start gap-2 p-3 bg-[#163A24]/5 border border-[#163A24]/20 rounded-lg text-xs text-[#163A24]">
              <Clock size={13} className="mt-0.5" />
              <span>Use o código OTP atual do app BirdID. Se expirar, volte e insira um novo código.</span>
            </div>
          )}

          <div className="flex gap-3">
            <button data-testid="btn-back-to-cert" onClick={() => setStep(1)}
              className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors">
              Voltar
            </button>
            <button data-testid="btn-sign-document" onClick={handleSign} disabled={signing}
              className="flex-1 flex items-center justify-center gap-2 bg-[#163A24] text-white py-3 rounded-md font-bold text-sm hover:bg-[#205234] disabled:opacity-60 transition-all duration-200 shadow-sm">
              {signing ? <><Loader2 size={16} className="animate-spin" /> Assinando...</> : <><Key size={16} /> Assinar Documento</>}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Done */}
      {step === 3 && result && (
        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-8 text-center space-y-5" data-testid="step-done">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto">
            <ShieldCheck size={32} className="text-green-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Outfit, sans-serif" }}>Documento Assinado!</h3>
            <p className="text-gray-500 text-sm mt-1">
              {result.demo_mode ? "Assinado em modo demonstração" : "Assinatura digital ICP-Brasil aplicada com sucesso"}
            </p>
          </div>
          <div className="bg-[#FAF9F6] rounded-lg p-4 text-left space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Assinante</span>
              <span className="font-medium text-gray-800">{result.signer_cn?.split(":")[0] || "Certificado Demo"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Padrão</span>
              <span className="font-semibold text-[#163A24]">{result.sign_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Data/Hora</span>
              <span className="text-gray-600">{new Date(result.signed_at).toLocaleString("pt-BR")}</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button data-testid="btn-download-signed" onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 bg-[#163A24] text-white py-3 rounded-md font-semibold text-sm hover:bg-[#205234] transition-all duration-200">
              <Download size={16} /> Baixar Documento Assinado
            </button>
            <button data-testid="btn-sign-another" onClick={() => { setStep(0); setFile(null); setDocId(null); setResult(null); setError(null); navigate("/sign"); }}
              className="px-4 py-3 border border-gray-200 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors">
              Novo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- BirdID Form Component ---
function BirdIDForm({ otp, setOtp, certificates, selectedCert, setSelectedCert }) {
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  const handleDigit = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otpDigits];
    next[i] = val.slice(-1);
    setOtpDigits(next);
    const full = next.join("");
    setOtp(full);
    if (val && i < 5) refs[i + 1].current?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otpDigits[i] && i > 0) refs[i - 1].current?.focus();
    if (e.key === "ArrowLeft" && i > 0) refs[i - 1].current?.focus();
    if (e.key === "ArrowRight" && i < 5) refs[i + 1].current?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length >= 6) {
      const digits = pasted.split("");
      setOtpDigits(digits);
      setOtp(pasted);
      refs[5].current?.focus();
      e.preventDefault();
    }
  };

  return (
    <div className="space-y-4">
      {/* Instructions */}
      <div className="bg-[#163A24] text-white rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Smartphone size={20} className="text-[#8FBC8F] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Como obter o código OTP</p>
            <ol className="text-xs text-white/80 space-y-1.5">
              <li className="flex items-start gap-2"><span className="w-4 h-4 bg-[#8FBC8F] text-[#163A24] rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5">1</span>Abra o <strong className="text-white">app BirdID</strong> no seu celular</li>
              <li className="flex items-start gap-2"><span className="w-4 h-4 bg-[#8FBC8F] text-[#163A24] rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5">2</span>O código de <strong className="text-white">6 dígitos</strong> aparece na tela principal</li>
              <li className="flex items-start gap-2"><span className="w-4 h-4 bg-[#8FBC8F] text-[#163A24] rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5">3</span>Digite o código abaixo antes de expirar (30s)</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Certificate select */}
      {certificates.length > 1 && (
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Certificado</label>
          <select data-testid="cert-select" value={selectedCert || ""} onChange={(e) => setSelectedCert(e.target.value)}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#163A24] bg-white">
            {certificates.map((c) => (
              <option key={c.alias} value={c.alias}>{c.cn || c.label} {c.mock ? "(Simulação)" : ""}</option>
            ))}
          </select>
        </div>
      )}

      {/* OTP Input */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Código OTP do BirdID</label>
          <OtpTimer />
        </div>
        <div className="flex gap-2 justify-center" data-testid="otp-input-group" onPaste={handlePaste}>
          {otpDigits.map((d, i) => (
            <React.Fragment key={i}>
              <input
                ref={refs[i]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleDigit(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                data-testid={`otp-digit-${i}`}
                className={`w-11 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:border-[#163A24] transition-all duration-200 ${d ? "border-[#163A24] bg-[#163A24]/5 text-[#163A24]" : "border-gray-200 text-gray-600"}`}
              />
              {i === 2 && <span className="text-gray-300 text-2xl self-center">-</span>}
            </React.Fragment>
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">Você também pode colar o código (Ctrl+V)</p>
        <p className="text-center text-[10px] text-gray-500 mt-2 bg-gray-50 p-2 rounded-lg border border-gray-100">
          <strong>Dica:</strong> Se o seu Agente Local já abre a janela do BirdID automaticamente, você pode deixar este campo em branco.
        </p>
      </div>
    </div>
  );
}

// --- A3 Token Form Component ---
function A3TokenForm({ pin, setPin, certificates, selectedCert, setSelectedCert }) {
  return (
    <div className="space-y-4">
      {certificates.length > 0 && (
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Certificado no Token</label>
          <select data-testid="cert-select" value={selectedCert || ""} onChange={(e) => setSelectedCert(e.target.value)}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#163A24] bg-white">
            {certificates.map((c) => (
              <option key={c.alias} value={c.alias}>{c.cn || c.label} {c.mock ? "(Simulação)" : ""}</option>
            ))}
          </select>
          {certificates.find(c => c.alias === selectedCert) && (
            <p className="text-xs text-gray-400 mt-1.5">
              Validade: {new Date(certificates.find(c => c.alias === selectedCert)?.valid_to).toLocaleDateString("pt-BR")}
            </p>
          )}
        </div>
      )}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">PIN do Cartão/Token A3</label>
        <input type="password" data-testid="pin-input" value={pin} onChange={(e) => setPin(e.target.value)}
          placeholder="Digite o PIN do seu token" autoComplete="off"
          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#163A24]" />
      </div>
    </div>
  );
}
